import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, MapPin, Clock, Briefcase, ArrowUpRight, Filter } from 'lucide-react';

const jobsData = [
  { id: 1, title: 'Senior Full-Stack Developer', department: 'Engineering', location: 'Remote', type: 'Full-Time' },
  { id: 2, title: 'Frontend Systems Engineer (React)', department: 'Engineering', location: 'New York, NY', type: 'Full-Time' },
  { id: 3, title: 'Lead UI/UX Designer', department: 'Design', location: 'Remote', type: 'Full-Time' },
  { id: 4, title: 'Cloud Solutions Architect', department: 'Infrastructure', location: 'San Francisco, CA', type: 'Contract' },
  { id: 5, title: 'Senior Product Manager', department: 'Product', location: 'London, UK', type: 'Full-Time' },
  { id: 6, title: 'Site Reliability / DevOps', department: 'Engineering', location: 'Hybrid', type: 'Full-Time' }
];

const departments = ['All', 'Engineering', 'Design', 'Infrastructure', 'Product'];

const Careers = () => {
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');

  const filteredJobs = jobsData.filter(job => {
    const matchesFilter = filter === 'All' || job.department === filter;
    const matchesSearch = job.title.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="pt-24 bg-transparent min-h-screen relative overflow-hidden">
      
      {/* Immersive Careers Header */}
      <div className="container mx-auto px-6 max-w-7xl pt-16 pb-12 relative z-10 text-center">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 text-slate-700 font-semibold text-sm mb-8"
        >
          <Briefcase size={16} /> Open Positions
        </motion.div>
        
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-5xl md:text-7xl font-extrabold text-slate-900 mb-6 tracking-tight"
        >
          Build what <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">matters.</span>
        </motion.h1>
        
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed"
        >
          Join a globally distributed team of elite engineers, designers, and strategists crafting the future of enterprise software.
        </motion.p>
      </div>

      <div className="container mx-auto px-6 max-w-5xl relative z-10 pb-32">
        
        {/* Search & Filter Controls */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/80 backdrop-blur-md rounded-2xl p-4 md:p-6 shadow-sm border border-slate-200/60 flex flex-col md:flex-row gap-4 mb-12"
        >
          <div className="relative flex-grow">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <input 
              type="text" 
              placeholder="Search positions (e.g. Frontend developer)" 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 text-slate-900 rounded-xl py-3 pl-12 pr-4 focus:outline-none focus:ring-2 focus:ring-blue-600/50 transition-shadow"
            />
          </div>
          
          <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 scrollbar-hide">
            {departments.map((dept) => (
              <button
                key={dept}
                onClick={() => setFilter(dept)}
                className={`whitespace-nowrap px-6 py-3 rounded-xl font-medium transition-colors ${
                  filter === dept 
                    ? 'bg-slate-900 text-white shadow-md' 
                    : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'
                }`}
              >
                {dept}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Dynamic Job Board List */}
        <div className="flex flex-col gap-4">
          <AnimatePresence>
            {filteredJobs.length === 0 ? (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-20 bg-white/50 backdrop-blur-sm rounded-3xl border border-slate-200/50 block"
              >
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-400">
                  <Filter size={24} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">No positions found</h3>
                <p className="text-slate-500">Try adjusting your search filters or check back later.</p>
              </motion.div>
            ) : (
              filteredJobs.map((job, index) => (
                <motion.div
                  layout
                  key={job.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3 }}
                  className="group bg-white rounded-2xl p-6 border border-slate-200/60 shadow-sm hover:shadow-xl hover:border-blue-200 transition-all cursor-pointer flex flex-col md:flex-row items-start md:items-center justify-between"
                >
                  <div className="mb-6 md:mb-0">
                    <div className="text-xs font-bold text-blue-600 tracking-wider uppercase mb-2">
                       {job.department}
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors">
                      {job.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-4 text-slate-500 text-sm font-medium">
                      <span className="flex items-center gap-1.5"><MapPin size={16} className="text-slate-400" /> {job.location}</span>
                      <span className="flex items-center gap-1.5"><Clock size={16} className="text-slate-400" /> {job.type}</span>
                    </div>
                  </div>
                  
                  <button className="flex items-center gap-2 bg-slate-50 group-hover:bg-blue-600 text-slate-700 group-hover:text-white px-6 py-3 rounded-full font-semibold transition-colors shrink-0 w-full md:w-auto justify-center">
                    Apply Now
                    <ArrowUpRight size={18} />
                  </button>
                </motion.div>
              ))
            )}
          </AnimatePresence>
        </div>

      </div>
    </div>
  );
};

export default Careers;
